<template>
    <div
        ref="modal"
        id="modal-bundle-bag"
        class="fixed z-60 left-0 bottom-0 w-full h-[calc(100dvh_-_1.25rem)] rounded-t-[2rem] bg-uiWhite shadow-[0rem_-0.125rem_0.625rem_0rem_rgba(178,178,178,0.25),0rem_-0.6875rem_0.375rem_0rem_rgba(74,74,74,0.01),0rem_-0.1875rem_0.375rem_0rem_rgba(74,74,74,0.09)] box-border transition-transform duration-300 large:hidden"
        :class="[isActive ? 'translate-y-0' : 'translate-y-[calc(100%_-_6.0625rem)]']"
        aria-labelledby="modal-bundle-bag-heading"
    >
        <div class="sticky z-1 top-0 left-0 pt-[0.9375rem] px-[1.375rem] pb-5">
            <div class="flex items-center justify-between gap-5 large:gap-10">
                <div class="flex items-center gap-x-[0.4375rem] body-xs uppercase">
                    <span>
                        1. {{ $root.config.translations.mini_bag.step_one_heading }}
                    </span>
                    <span class="text-uiDarkGrey">
                        |
                    </span>
                    <span :class="{'text-uiDarkGrey': activeStep < 2}">
                        2. {{ $root.config.translations.mini_bag.step_two_heading }}
                    </span>
                    <span class="text-uiDarkGrey">
                        |
                    </span>
                    <span :class="{'text-uiDarkGrey': activeStep < 3}">
                        3. {{ $root.config.translations.mini_bag.step_three_heading }}
                    </span>
                </div>
                <button
                    class="text-white rounded-full py-2 px-[0.375rem] bg-primary ml-auto"
                    type="button"
                    :aria-label="$root.config.translations.accessibility.close_overlay"
                    aria-controls="modal-bundle-bag"
                    :aria-expanded="isActive"
                    @click="toggleModal()"
                >
                    <svg
                        class="transition-transform duration-300"
                        :class="{'rotate-180': isActive}"
                        width="10"
                        height="6"
                        viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                    >
                        <path class="fill-current" fill d="M9.50158 5.19732C9.50158 5.28738 9.46694 5.37744 9.39074 5.44672C9.25218 5.57835 9.0305 5.57142 8.89887 5.43286L4.99166 1.31782L1.09138 5.43286C0.959753 5.57142 0.744995 5.57835 0.606442 5.44672C0.467888 5.31509 0.46096 5.09341 0.592586 4.95486L4.74919 0.569635C4.81154 0.500358 4.90853 0.458792 4.99859 0.458792C5.08865 0.458792 5.18564 0.500358 5.24799 0.569635L9.40459 4.96178C9.46694 5.03106 9.50158 5.11419 9.50158 5.19732Z" />
                    </svg>
                </button>
            </div>
            <div class="flex items-center justify-between gap-5 mt-3 large:gap-10">
                <div class="flex items-center gap-x-1">
                    <h2
                        id="modal-bundle-bag-heading"
                        class="heading-xxs"
                    >
                        {{ $root.config.translations.mini_bag.heading }}
                    </h2>
                    <span class="body-r">
                        ({{ productsInBundle }})
                    </span>
                </div>
                <div class="flex items-center gap-x-[0.875rem]">
                    <template v-if="bundleBagPriceTotalCompareAtCurrency !== bundleBagPriceTotalCurrency">
                        <span class="body-r">
                            <s>
                                {{ bundleBagPriceTotalCompareAtCurrency }}
                            </s>
                        </span>
                        <span class="heading-xxs">
                            {{ bundleBagPriceTotalCurrency }}
                        </span>
                    </template>
                    <template v-else>
                        <span class="heading-xxs">
                            {{ bundleBagPriceTotalCurrency }}
                        </span>
                    </template>
                </div>
            </div>
        </div>
        <div
            ref="content"
            class="h-[calc(100%_-_11.25rem)] py-4 border-t border-uiMidGrey overflow-y-auto"
            :class="[isActive ? 'translate-y-0' : 'translate-y-full']"
        >
            <div class="px-[1.375rem]">
                <template v-if="activeStep === 1">
                    <div
                        v-if="$root.config.schema.bundle_min_spend_threshold && !minSpendReached"
                        class="flex items-center gap-x-2"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 11C12.5523 11 13 11.4477 13 12V16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16V12C11 11.4477 11.4477 11 12 11Z" fill="#FFAD32" />
                            <path d="M12 7C11.4477 7 11 7.44772 11 8C11 8.55228 11.4477 9 12 9H12.01C12.5623 9 13.01 8.55228 13.01 8C13.01 7.44772 12.5623 7 12.01 7H12Z" fill="#FFAD32" />
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3Z" fill="#FFAD32" />
                        </svg>
                        <span class="body-r">
                            {{ minSpendWarningText }}
                        </span>
                    </div>
                    <template v-if="productBundle">
                        <div
                            v-for="(bundleProductData, index) in productBundle"
                            :key="index"
                            class="py-4 border-b border-uiDarkGrey first-of-type:pt-0 last-of-type:border-b-0"
                        >
                            <div class="flex items-center justify-between gap-5">
                                <div>
                                    <p v-if="bundleProductData.product?.vendor" class="body-xs">
                                        {{ bundleProductData.product.vendor }}
                                    </p>
                                    <p v-if="bundleProductData.product?.name" class="body-m">
                                        {{ bundleProductData.product.name }}
                                    </p>
                                    <p
                                        v-if="bundleProductData.product?.options[0] && bundleProductData.product?.variants?.length > 1 && selectedVariant(bundleProductData)"
                                        class="body-s"
                                    >
                                        {{ bundleProductData.product.options[0] }}: {{ selectedVariant(bundleProductData).title }}
                                    </p>
                                    <div class="flex items-center gap-[0.625rem] mt-[0.625rem]">
                                        <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0]">
                                            <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0].compare_at_price && selectedVariant(bundleProductData).selling_plan_allocations[0].price">
                                                <span>
                                                    <s>
                                                        {{ (selectedVariant(bundleProductData).selling_plan_allocations[0].compare_at_price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                    </s>
                                                </span>
                                                <span>
                                                    {{ (selectedVariant(bundleProductData).selling_plan_allocations[0].price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                </span>
                                            </template>
                                            <template v-else-if="selectedVariant(bundleProductData).price">
                                                <div class="flex items-center gap-x-2">
                                                    <span>
                                                        {{ (selectedVariant(bundleProductData).price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                    </span>
                                                    <span class="body-xs py-[0.125rem] px-[0.375rem] rounded-[0.25rem] bg-uiGrey">
                                                        {{ $root.config.translations.mini_bag.excludes_bundle_savings_tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <div class="flex items-center gap-x-2">
                                                <span>
                                                    {{ (selectedVariant(bundleProductData).price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                </span>
                                                <span class="body-xs py-[0.125rem] px-[0.375rem] rounded-[0.25rem] bg-uiGrey">
                                                    {{ $root.config.translations.mini_bag.excludes_bundle_savings_tag }}
                                                </span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div>
                                    <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0]">
                                        <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0].price">
                                            {{ ((selectedVariant(bundleProductData).selling_plan_allocations[0].price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                        </template>
                                        <template v-else-if="selectedVariant(bundleProductData).price">
                                            <span>
                                                {{ ((selectedVariant(bundleProductData).price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                            </span>
                                        </template>
                                    </template>
                                    <template v-else>
                                        <template v-if="selectedVariant(bundleProductData).price">
                                            {{ ((selectedVariant(bundleProductData).price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                        </template>
                                    </template>
                                </div>
                            </div>
                            <div class="flex items-center gap-5 mt-4">
                                <div class="quantity quantity--mini">
                                    <button
                                        type="button"
                                        class="quantity__button"
                                        :aria-label="$root.config.translations.product_cards.decrease_quantity"
                                        @click="amendQuantity(index, -1)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor" />
                                        </svg>
                                    </button>
                                    <span class="quantity__input">
                                        {{ bundleProductData.quantity }}
                                    </span>
                                    <button
                                        type="button"
                                        class="quantity__button"
                                        :aria-label="$root.config.translations.product_cards.increase_quantity"
                                        @click="amendQuantity(index, 1)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor" />
                                        </svg>
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    class="flex items-center justify-center w-[3.375rem] h-[3.375rem] rounded-full text-primary bg-uiWhite transition-colors duration-300 hover:text-uiWhite focus:text-uiWhite hover:bg-primary focus:bg-primary"
                                    :aria-label="$root.config.translations.product_cards.remove_from_bundle"
                                    @click="removeProductFromBundle(index)"
                                >
                                    <svg  aria-hidden="true" focusable="false" role="presentation" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.8075 2.85181C11.8075 2.29962 11.3596 1.85196 10.8075 1.85181H6.65708C6.1048 1.85181 5.65708 2.29952 5.65708 2.85181V3.55591H11.8075V2.85181ZM4.65708 2.85181V3.68514H0.582031C0.305889 3.68514 0.0820312 3.909 0.0820312 4.18514C0.0820312 4.46128 0.305889 4.68514 0.582031 4.68514H2.21218L2.21218 15.1481C2.21218 16.2527 3.10761 17.1481 4.21218 17.1481H13.2522L13.4563 17.1374C14.465 17.0351 15.2522 16.1838 15.2522 15.1481L15.2522 4.68514H16.8818C17.158 4.68514 17.3818 4.46128 17.3818 4.18514C17.3818 3.909 17.158 3.68514 16.8818 3.68514H12.8075V2.85181C12.8075 1.81625 12.0201 0.964943 11.0116 0.862549L10.8075 0.851807H6.65708C5.55251 0.851807 4.65708 1.74724 4.65708 2.85181ZM4.21218 16.1481C3.65989 16.1481 3.21218 15.7004 3.21218 15.1481L3.21218 4.81509L14.2522 4.81509L14.2522 15.1481C14.2522 15.7004 13.8045 16.1481 13.2522 16.1481L4.21218 16.1481Z" fill="currentColor"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </template>
                </template>
                <template v-if="activeStep === 2">
                    <p class="heading-xxs">
                        {{ $root.config.translations.mini_bag.step_two_content_title }}
                    </p>
                    <p class="mt-[0.625rem]">
                        {{ $root.config.translations.mini_bag.step_two_content_description }}
                    </p>
                    <div class="mt-4">
                        <fieldset>
                            <legend
                                class="sr-only"
                            >
                                {{ $root.config.translations.mini_bag.step_two_fieldset_legend }}
                            </legend>
                            <div
                                v-for="(sellingPlan, index) in productBundle[0].product.selling_plans"
                                :key="index"
                                class="relative p-3 border border-primary rounded-[0.25rem] mb-2 last-of-type:mb-0"
                            >
                                <input
                                    type="radio"
                                    :id="`selling_plan_${sellingPlan.id}`"
                                    class="delivery-radio-input opacity-0 cursor-pointer absolute top-0 left-0 w-full h-full [&:checked_+_label]:after:visible"
                                    name="selling_plan"
                                    :value="sellingPlan.id"
                                    v-model="selectedSellingPlan"
                                    @change="updateSelectedPlanIndex(index)"
                                >
                                <label
                                    :for="`selling_plan_${sellingPlan.id}`"
                                    class="
                                        delivery-radio-label
                                        relative body-m pl-[1.875rem] box-border cursor-pointer
                                        before:content-[''] before:absolute before:top-1/2 before:left-0 before:-translate-y-1/2 before:block before:w-5 before:h-5 before:border before:border-primary before:rounded-full
                                        after:content-[''] after:absolute after:top-1/2 after:left-0 after:-translate-y-1/2 after:block after:w-5 after:h-5 after:border-[0.375rem] after:border-primary after:rounded-full after:bg-uiWhite after:invisible
                                    "
                                >
                                    {{ sellingPlan.name }}
                                </label>
                            </div>
                        </fieldset>
                    </div>
                </template>
                <template v-if="activeStep === 3">
                    <div class="flex items-center justify-between gap-5">
                        <p class="heading-xxs">
                            {{ $root.config.translations.mini_bag.step_three_content_title }}
                            <span>
                                ({{ productsInBundle }})
                            </span>
                        </p>
                        <button
                            type="button"
                            class="link"
                            @click="setActiveStep(1)"
                        >
                            {{ $root.config.translations.mini_bag.edit_cta }}
                        </button>
                    </div>
                    <div v-if="productBundle">
                        <div
                            v-for="(bundleProductData, index) in productBundle"
                            :key="index"
                            class="py-[0.625rem] border-b border-uiDarkGrey last-of-type:border-b-uiBlack"
                        >
                            <div class="flex items-center justify-between gap-5">
                                <div>
                                    <p v-if="bundleProductData.product?.vendor" class="body-xs">
                                        {{ bundleProductData.product.vendor }}
                                    </p>
                                    <p v-if="bundleProductData.product?.name" class="body-m">
                                        {{ bundleProductData.product.name }}
                                    </p>
                                    <p
                                        v-if="bundleProductData.product?.options[0] && bundleProductData.product?.variants?.length > 1 && selectedVariant(bundleProductData)"
                                        class="body-s"
                                    >
                                        {{ bundleProductData.product.options[0] }}: {{ selectedVariant(bundleProductData).title }}
                                    </p>
                                    <p v-if="bundleProductData.quantity" class="body-xs">
                                        {{ $root.config.translations.mini_bag.quantity_shorthand }}: {{ bundleProductData.quantity }}
                                    </p>
                                    <div class="flex items-center gap-[0.625rem] mt-[0.625rem]">
                                        <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0]">
                                            <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0].compare_at_price && selectedVariant(bundleProductData).selling_plan_allocations[0].price">
                                                <span>
                                                    <s>
                                                        {{ (selectedVariant(bundleProductData).selling_plan_allocations[0].compare_at_price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                    </s>
                                                </span>
                                                <span>
                                                    {{ (selectedVariant(bundleProductData).selling_plan_allocations[0].price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                </span>
                                            </template>
                                            <template v-else-if="selectedVariant(bundleProductData).price">
                                                <div class="flex items-center gap-x-2">
                                                    <span>
                                                        {{ (selectedVariant(bundleProductData).price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                    </span>
                                                    <span class="body-xs py-[0.125rem] px-[0.375rem] rounded-[0.25rem] bg-uiGrey">
                                                        {{ $root.config.translations.mini_bag.excludes_bundle_savings_tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <div class="flex items-center gap-x-2">
                                                <span>
                                                    {{ (selectedVariant(bundleProductData).price / 100).toFixed(2) }}{{ currencySymbol[0] }}
                                                </span>
                                                <span class="body-xs py-[0.125rem] px-[0.375rem] rounded-[0.25rem] bg-uiGrey">
                                                    {{ $root.config.translations.mini_bag.excludes_bundle_savings_tag }}
                                                </span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div>
                                    <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0]">
                                        <template v-if="selectedVariant(bundleProductData).selling_plan_allocations[0].price">
                                            {{ ((selectedVariant(bundleProductData).selling_plan_allocations[0].price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                        </template>
                                        <template v-else-if="selectedVariant(bundleProductData).price">
                                            <span>
                                                {{ ((selectedVariant(bundleProductData).price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                            </span>
                                        </template>
                                    </template>
                                    <template v-else>
                                        <template v-if="selectedVariant(bundleProductData).price">
                                            {{ ((selectedVariant(bundleProductData).price / 100).toFixed(2) * bundleProductData.quantity).toFixed(2) }}{{ currencySymbol[0] }}
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-5">
                        <div class="flex items-center justify-between gap-5">
                            <p class="heading-xxs">
                                {{ $root.config.translations.mini_bag.delivery }}
                            </p>
                            <button
                                type="button"
                                class="link"
                                @click="setActiveStep(2)"
                            >
                                {{ $root.config.translations.mini_bag.edit_cta }}
                            </button>
                        </div>
                        <p
                            v-if="productBundle[0]?.product?.selling_plans[selectedSellingPlanIndex]?.name"
                            class="body-m mt-2"
                        >
                            {{ productBundle[0].product.selling_plans[selectedSellingPlanIndex].name }}
                        </p>
                    </div>
                </template>
            </div>
        </div>
        <div class="fixed z-1 left-0 bottom-0 w-full py-[0.875rem] px-[1.375rem] border-t border-uiMidGrey bg-uiWhite box-border">
            <div class="flex items-center justify-between">
                <button
                    v-if="activeStep != 1"
                    type="button"
                    class="link font-bold body-m text-primary"
                    @click="previousStep()"
                >
                    {{ $root.config.translations.mini_bag.back_cta }}
                </button>
                <button
                    v-if="activeStep != 3"
                    type="button"
                    class="button min-w-[10.875rem] justify-center ml-auto"
                    :class="{'button--primary': productsInBundle >= 1 || minSpendReached}"
                    :disabled="productsInBundle < 1 || !minSpendReached || !selectedSellingPlan && activeStep == 2"
                    @click="nextStep()"
                >
                    {{ $root.config.translations.mini_bag.next_cta }}
                </button>
                <button
                    v-else-if="activeStep === 3"
                    type="button"
                    class="button button--primary min-w-[15rem] justify-center"
                    @click="addToCart()"
                >
                    {{ $root.config.translations.mini_bag.add_to_cart }} | {{ bundleBagPriceTotalCurrency }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        emits: [
            'amended-bundle-item-quantity',
            'removed-bundle-item'
        ],
        props: {
            bundleBagPriceTotal: {
                type: Number,
                default() {
                    return null;
                }
            },
            bundleBagPriceTotalCurrency: {
                type: String,
                default() {
                    return null;
                }
            },
            bundleBagPriceTotalCompareAtCurrency: {
                type: String,
                default() {
                    return null;
                }
            },
            currencySymbol: {
                type: Array,
                default () {
                    return [];
                }
            },
            productBundle: {
                type: Array,
                default () {
                    return [];
                }
            }
        },
        data () {
            return {
                activeStep: 1,
                isActive: false,
                selectedSellingPlan: null,
                selectedSellingPlanIndex: null
            };
        },
        mounted () {
            document.addEventListener('keydown', event => {
                if (this.isActive && event.key === 'Escape') {
                    event.preventDefault();
                    this.closeModal();
                }
            });
        },
        computed: {
            minSpendReached () {
                return this.bundleBagPriceTotal >= this.$root.config.schema.bundle_min_spend_threshold;
            },
            minSpendWarningText () {
                const amountFromMinSpend = this.$root.config.schema.bundle_min_spend_threshold - this.bundleBagPriceTotal;

                return `${this.$root.config.schema.bundle_min_spend_text_before} ${this.$root.config.schema.bundle_min_spend_threshold}${this.currencySymbol[0]}. ${this.$root.config.schema.bundle_min_spend_text_after.replace(/\[.*?\]/, `${amountFromMinSpend.toFixed(2)}${this.currencySymbol[0]}`)}`;
            },
            productsInBundle () {
                return this.productBundle.length;
            }
        },
        methods: {
            addToCart () {
                const dateTime = new Date();
                const dateNow = dateTime.toLocaleTimeString();
                let products = [];

                this.productBundle.forEach(productData => {
                    const data = {
                        'id': productData.variantId,
                        'quantity': productData.quantity,
                        'selling_plan': this.selectedVariant(productData)?.selling_plan_allocations[this.selectedSellingPlanIndex]?.selling_plan_id ? this.selectedVariant(productData).selling_plan_allocations[this.selectedSellingPlanIndex].selling_plan_id : null,
                        'title': productData.product.name,
                        'properties': {
                            '_is_subscription_bundle': true,
                            '_bundle_compare_at_price': this.bundleBagPriceTotalCompareAtCurrency,
                            '_timestamp': dateNow
                        }
                    }

                    products.push(data);
                });

                if (products.length) {
                    const cartNotification = document.querySelector('cart-notification');
                    cartNotification.addToCart(products);

                    this.closeModal();
                }
            },
            amendQuantity (index, change) {
                this.$emit('amended-bundle-item-quantity', index, change);
            },
            closeModal () {
                this.isActive = false;

                document.body.classList.remove('overflow-hidden');
            },
            nextStep () {
                if (this.activeStep >= 3) return;

                this.activeStep++;
            },
            previousStep () {
                if (this.activeStep <= 1) return;

                this.activeStep--;
            },
            removeProductFromBundle (index) {
                // Reset selected selling plan data
                this.selectedSellingPlan = null;
                this.selectedSellingPlanIndex = null;

                this.$emit('removed-bundle-item', index);
            },
            selectedVariant (bundleProductData) {
                // Return data for the selected variant
                return bundleProductData.product.variants.find((variant) => variant.id === Number(bundleProductData.variantId));
            },
            setActiveStep (step) {
                this.activeStep = step;
            },
            toggleModal () {
                this.isActive = !this.isActive;

                if (document.body.classList.contains('overflow-hidden')) {
                    document.body.classList.remove('overflow-hidden');
                } else {
                    document.body.classList.add('overflow-hidden');
                }
            },
            updateSelectedPlanIndex (index) {
                this.selectedSellingPlanIndex = index;
            }
        }
    }
</script>
